// ─────────────────────────────────────────────────────────────────────────────
// Crew — app orchestrator (Agent B). Owns auth gate, session load, hash routing,
// first-run consent, and the in-memory funnel state the no-list-endpoint API needs.
//
// REMOUNT-SAFE: main.tsx (frozen) re-renders the whole tree on every `hashchange`,
// which REMOUNTS <App/>. So we cache the resolved session (me + group detail +
// consent) at MODULE scope and hydrate from it synchronously on mount — a hash
// navigation never re-flashes the loading spinner or re-fetches needlessly. The
// server stays the source of truth; this is purely a remount/UX cache.
//
// VIEW MAP (hash → view): the sub-router (lib/router.ts) maps '' / '/' → home,
// '/group' → group, '/activities' → activities, '/settings' → settings, and
// returns null for #/metrics + #/privacy (Agent D's reserved surfaces). On top of
// the route we gate on session: no token → AuthScreen; token but no group →
// Onboarding; in a group → AppShell wrapping GroupHome / Activities / Settings.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import { api, isAuthed } from './lib/api';
import type { Consent, Goal, Group, Member, TimePoll } from './lib/types';
import type { BookingIntent } from './lib/types';
import { navigate, useHashRoute, type Route } from './lib/router';
import { loadFunnelState, saveFunnelState, clearFunnelState, type FunnelState } from './lib/funnelState';
import { refreshConsentSnapshot } from './lib/analytics';
import { AppShell } from './components/AppShell';
import { ConsentBanner } from './components/ConsentBanner';
import { AuthScreen } from './features/AuthScreen';
import { Onboarding } from './features/Onboarding';
import { GroupHome } from './features/GroupHome';
import { Activities } from './features/Activities';
import { Settings } from './features/Settings';
import { ErrorBanner, Spinner } from './components/Feedback';
import { errorMessage } from './lib/format';

// ── Resolved-session shape held in the module cache + React state ───────────────
interface Session {
  email: string;
  me: Member | null;
  group: Group | null; // summary from /me
  members: Member[]; // from /group (empty until loaded)
  consents: Consent[]; // from /consent (empty array if none yet)
}

// Module-level cache so a hashchange-driven remount rehydrates instantly instead of
// re-fetching /me + /group + /consent and re-showing the spinner. Reset on sign-out.
let sessionCache: Session | null = null;

function resetSessionCache(): void {
  sessionCache = null;
}

export default function App() {
  const route = useHashRoute(); // null when the hash is reserved (#/metrics, #/privacy)

  // Hydrate synchronously from the module cache so remounts don't flash loading.
  const [session, setSession] = useState<Session | null>(() => sessionCache);
  const [loading, setLoading] = useState(() => isAuthed() && sessionCache === null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  // Funnel state (goal / poll / intent) — no GET-list endpoints exist, so we hold
  // the created entities here and mirror them to sessionStorage per group.
  const [funnel, setFunnel] = useState<FunnelState>({ goal: null, poll: null, intent: null });

  // Consent banner: shown on first run only (no analytics choice recorded yet) and
  // dismissed for this session once the user makes any choice.
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [bannerBusy, setBannerBusy] = useState(false);

  // ── Load (or reload) the full session: /me → /group → /consent ───────────────
  const loadSession = useCallback(async () => {
    setLoadError(null);
    if (!isAuthed()) {
      sessionCache = null;
      setSession(null);
      setLoading(false);
      return;
    }
    setLoading(sessionCache === null);
    try {
      const meRes = await api.me();
      // In a group? Pull members (and entitlement, for future gating) + consent.
      let members: Member[] = [];
      if (meRes.group) {
        const groupRes = await api.getGroup(meRes.group.id);
        members = groupRes.members;
      }
      let consents: Consent[] = [];
      try {
        consents = (await api.getConsent()).consents;
      } catch {
        // Consent fetch is non-fatal for rendering the app; treat as "none yet".
        consents = [];
      }
      const next: Session = {
        email: meRes.email,
        me: meRes.member,
        group: meRes.group,
        members,
        consents,
      };
      sessionCache = next;
      setSession(next);
      // Hydrate funnel continuity for this group from sessionStorage.
      setFunnel(meRes.group ? loadFunnelState(meRes.group.id) : { goal: null, poll: null, intent: null });
    } catch (err) {
      // A 401 means the stored token is stale — drop it and fall back to auth.
      if (typeof err === 'object' && err !== null && 'status' in err && (err as { status: number }).status === 401) {
        api.signOut();
        resetSessionCache();
        setSession(null);
      } else {
        setLoadError(errorMessage(err, 'Could not load your account. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSession();
  }, [loadSession, retryKey]);

  // ── Persist funnel state whenever it changes (per group) ─────────────────────
  useEffect(() => {
    if (session?.group) saveFunnelState(session.group.id, funnel);
  }, [funnel, session?.group]);

  // ── Auth + onboarding transitions ────────────────────────────────────────────
  const onAuthed = useCallback(() => {
    resetSessionCache();
    setLoading(true);
    setRetryKey((k) => k + 1);
  }, []);

  const onJoined = useCallback(() => {
    resetSessionCache();
    setLoading(true);
    setRetryKey((k) => k + 1);
    navigate('group');
  }, []);

  const onSignOut = useCallback(() => {
    if (session?.group) clearFunnelState(session.group.id);
    api.signOut();
    refreshConsentSnapshot(); // next session re-reads consent from scratch
    resetSessionCache();
    setSession(null);
    setFunnel({ goal: null, poll: null, intent: null });
    setBannerDismissed(false);
    navigate('home');
  }, [session?.group]);

  // ── First-run consent choice (from the banner) ───────────────────────────────
  async function chooseAnalytics(granted: boolean) {
    setBannerBusy(true);
    try {
      const res = await api.setConsent('analytics', granted);
      // Reflect the new consent set locally and invalidate the SDK snapshot.
      setSession((s) => (s ? { ...s, consents: res.consents } : s));
      if (sessionCache) sessionCache = { ...sessionCache, consents: res.consents };
      refreshConsentSnapshot();
    } catch {
      // Non-fatal: leave the banner choice for next time; analytics stays off.
    } finally {
      setBannerBusy(false);
      setBannerDismissed(true);
    }
  }

  // ── Funnel state setters passed down to features ─────────────────────────────
  const setGoal = useCallback((goal: Goal) => setFunnel((f) => ({ ...f, goal })), []);
  const setPoll = useCallback((poll: TimePoll) => setFunnel((f) => ({ ...f, poll })), []);
  const setIntent = useCallback(
    (intent: BookingIntent | null) => setFunnel((f) => ({ ...f, intent })),
    [],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  // Reserved hash (#/metrics or #/privacy) — handled by main.tsx, not us. Defensive
  // no-op: returning null lets the parent surface render without our chrome.
  if (route === null) return null;

  // Loading the session (authed, cache cold).
  if (loading) {
    return (
      <main className="flex min-h-full items-center justify-center p-6">
        <Spinner label="Loading Crew…" />
      </main>
    );
  }

  // Hard load error (non-401) — offer a retry.
  if (loadError) {
    return (
      <main className="mx-auto flex min-h-full max-w-md items-center justify-center p-6">
        <div className="w-full">
          <ErrorBanner message={loadError} onRetry={() => setRetryKey((k) => k + 1)} />
        </div>
      </main>
    );
  }

  // Not signed in → magic-link auth.
  if (!isAuthed() || !session) {
    return (
      <main className="flex min-h-full flex-col px-4">
        <AuthScreen onAuthed={onAuthed} />
      </main>
    );
  }

  // Signed in, but not in a group yet → onboarding (create or join). Group
  // formation is NEVER monetisation-gated.
  if (!session.group || !session.me) {
    return (
      <main className="flex min-h-full flex-col px-4">
        <Onboarding onJoined={onJoined} />
      </main>
    );
  }

  // In a group → the funnel under the app shell.
  const group = session.group;
  const me = session.me;

  // Show the first-run consent banner only when the user has never made an analytics
  // choice (no analytics row recorded) and hasn't dismissed it this session.
  const analyticsChoiceMade = session.consents.some((c) => c.scope === 'analytics');
  const showBanner = !analyticsChoiceMade && !bannerDismissed;

  // The "home" route maps onto the group view once signed in (the landing surface
  // for a member is their group). Settings/Activities are the other tabs.
  const activeRoute: Route = route === 'home' ? 'group' : route;

  let view;
  if (activeRoute === 'activities') {
    view = (
      <Activities
        group={group}
        me={me}
        goalId={funnel.goal?.id}
        memberCount={session.members.length || group.memberIds.length || 1}
        intent={funnel.intent}
        onIntent={setIntent}
      />
    );
  } else if (activeRoute === 'settings') {
    view = <Settings onSignOut={onSignOut} />;
  } else {
    // 'group' (and the home alias)
    view = (
      <GroupHome
        group={group}
        members={session.members}
        me={me}
        inviteCode={group.inviteCode}
        goal={funnel.goal}
        poll={funnel.poll}
        onGoal={setGoal}
        onPoll={setPoll}
      />
    );
  }

  return (
    <>
      <AppShell route={activeRoute} showNav email={session.email} onSignOut={onSignOut}>
        {view}
      </AppShell>
      {showBanner && <ConsentBanner onChoice={chooseAnalytics} busy={bannerBusy} />}
    </>
  );
}
